import {
  CategoryOptionsInterface,
  ContractTypes,
  ContractTypesInterface,
  CourseSegment,
  CourseSegmentOptions,
  getCategoryOptions,
  WorkSchedule,
  CourseSegmentOptionsInterface,
} from '@duke-hq/shared-constants/enrolments';
import { skipToken } from '@tanstack/react-query';
import {
  Box,
  Button,
  ButtonGroup,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  HStack,
  Icon,
  Input,
  Skeleton,
  Text,
  Tooltip,
} from '@chakra-ui/react';
import { trpcTenant } from '@duke-hq/app-provider-web/tenant/trpcTenant';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { AsyncSelect, MultiValue, Select, SingleValue } from 'chakra-react-select';
import { AlertCircleIcon, ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { useMemo, useCallback } from 'react';
import { Controller, useController, useForm, useWatch } from 'react-hook-form';
import { useDebounceCallback } from 'usehooks-ts';
import { useSnapshot } from 'valtio';
import { djs } from '@duke-hq/djs';
import { Option } from '../../../components/Option';
import {
  CourseReferralModalMode,
  courseReferralModalState,
} from '../../tenantState/CourseReferralState';
import { useAuthTenant } from '../../useAuthTenant';
import {
  FormContentContainer,
  FormFooterContainer,
  FormMainContainer,
} from '@duke-hq/ui/components/Modals/FormModalLayouts';
import { EnrolmentSettingsDataTypes } from './types';
import { useTenantContext } from '@duke-hq/app-provider-web/tenant/TenantContext';
import { useSemesterEndDate } from '@duke-hq/ui/components/hooks/useSemesterEndDate';
import {
  ConsultantRoleSchema,
  SiteOption,
  ConsultantOption,
} from '@duke-hq/svc-api/provider/util/consultants';
import { getCourseDatesFromAdminCourseSchedule, toUtcStartOfDay } from '@duke-hq/lib/dates';
import { courseHasSemesterTwo } from '@duke-hq/shared-constants/course';
import { CourseKindSchema } from '@duke-hq/svc-api-db/schemas/enums/CourseKind.schema';

type EnrolmentSettingsProps = {
  goToNextStep: () => void;
  goToPrevStep: () => void;
};

export function EnrolmentSettings({ goToNextStep, goToPrevStep }: EnrolmentSettingsProps) {
  const auth = useAuthTenant();
  const courseReferralModalSnap = useSnapshot(courseReferralModalState);
  const tenantCtx = useTenantContext();
  const { selectedCourseAttributes } = courseReferralModalSnap;

  const { data: sitesList, isFetching: isSitesLoading } =
    trpcTenant.locations.listUserSiteAccess.useQuery({
      type: 'site',
    });

  const { data: course, isLoading } = trpcTenant.courses.courseSchedule.useQuery(
    selectedCourseAttributes.id,
  );

  const semester2StartDateMin = useSemesterEndDate({
    workSchedule: courseReferralModalSnap.enrolmentSettingsData.work_schedule,
    startAt: courseReferralModalSnap.enrolmentSettingsData.start_at_date,
  });

  const minStartDate = useMemo(() => {
    const today = djs();
    return semester2StartDateMin.isAfter(today) ? semester2StartDateMin : today;
  }, [semester2StartDateMin]);

  const {
    handleSubmit,
    register,
    control,
    getValues,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<EnrolmentSettingsDataTypes>({
    defaultValues: {
      ...courseReferralModalSnap.enrolmentSettingsData,
      start_at_date: djs(courseReferralModalSnap.enrolmentSettingsData.start_at_date).isValid()
        ? djs(courseReferralModalSnap.enrolmentSettingsData.start_at_date).format('YYYY-MM-DD')
        : djs().format('YYYY-MM-DD'),
      semester_2_start_at_date: (() => {
        const enrolmentDate =
          courseReferralModalSnap.enrolmentSettingsData.semester_2_start_at_date;
        const parsed = enrolmentDate ? djs(enrolmentDate) : null;

        if (parsed != null && parsed?.isValid() && !parsed.isBefore(minStartDate, 'day')) {
          return parsed.format('YYYY-MM-DD');
        }

        return minStartDate.format('YYYY-MM-DD');
      })(),
      course_segment: courseReferralModalSnap.enrolmentSettingsData.course_segment,
      enrolment_contract_type:
        courseReferralModalSnap.enrolmentSettingsData.enrolment_contract_type,
      contract_category: courseReferralModalSnap.enrolmentSettingsData.contract_category,
      selected_consultant:
        courseReferralModalSnap?.enrolmentSettingsData?.selected_consultant?.consultant_id !=
          null &&
        courseReferralModalSnap?.enrolmentSettingsData?.selected_consultant?.consultant_id != ''
          ? {
              consultant_id:
                courseReferralModalSnap?.enrolmentSettingsData?.selected_consultant.consultant_id,
              consultant_name:
                courseReferralModalSnap?.enrolmentSettingsData?.selected_consultant.consultant_name,
            }
          : {
              consultant_id: auth.user.id,
              consultant_name: `${auth.user.first_name} ${auth.user.last_name} (myself)`,
            },
    },
  });

  const today = djs();
  const [
    work_schedule,
    start_at_date,
    selected_site,
    selected_consultant,
    enrolment_contract_type,
    course_segment,
  ] = useWatch({
    control,
    name: [
      'work_schedule',
      'start_at_date',
      'selected_site',
      'selected_consultant',
      'enrolment_contract_type',
      'course_segment',
    ],
  });

  const { data: consultantsInSite, isFetching: isConsultantsLoading } =
    trpcTenant.locations.getConsultantsInSiteLocation.useQuery(
      selected_site?.site_id != null ? { siteLocationIds: [selected_site?.site_id] } : skipToken,
      {
        enabled:
          selected_site?.site_id != null &&
          tenantCtx?.permissions.includes('canCreateReferralsForTeamMembers'),
        initialData: [],
      },
    );

  const {
    field: { value: contractType, onChange: onContractTypeChange },
  } = useController({
    name: 'enrolment_contract_type',
    control,
  });

  const categoryOptions = getCategoryOptions(contractType);

  const sitesOptions = useMemo(() => {
    return (sitesList
      ?.map((site) => ({
        value: {
          site_id: site.id,
          site_name: `${site.name}, ${site.state}`,
          site_state: site.state,
        },
        label: site.state ? `${site.name}, ${site.state}` : site.name,
      }))
      .filter((option: unknown): option is SiteOption => option !== null) ||
      []) satisfies SiteOption[];
  }, [sitesList]);

  const loadSitesOptions = useDebounceCallback<
    (inputValue: string, callback: (opts: SiteOption[]) => void) => void
  >((inputValue, callback) => {
    if (sitesOptions.length > 0) {
      callback(
        sitesOptions
          .filter(
            (option) =>
              option.label.toLowerCase().includes(inputValue.toLowerCase()) ||
              option.value.site_name.toLowerCase().includes(inputValue.toLowerCase()),
          )
          .filter((option): option is SiteOption => option !== null),
      );

      // If there's only one site available, select it
      if (selected_site?.site_id != null && sitesOptions.length === 1) {
        const firstOption = sitesOptions[0];
        if (
          firstOption != null &&
          firstOption?.value != null &&
          typeof firstOption?.value?.site_state === 'string'
        ) {
          setValue('selected_site', {
            site_id: firstOption.value.site_id,
            site_name: firstOption.value.site_name,
            site_state: firstOption.value.site_state,
          });
        }
      }
    } else {
      callback([]);
    }
  }, 500);

  const consultantOptions = useMemo(() => {
    return consultantsInSite
      .map((user) => ({
        role: ConsultantRoleSchema.safeParse(user.role).data,
        email: user.email ?? '',
        value: user.id,
        label: `${user.first_name} ${user.last_name} ${
          auth.user.id === user.id ? `(${t`myself`})` : ''
        }`,
        fullname: `${user.first_name} ${user.last_name}`,
      }))
      .filter((option): option is ConsultantOption => option !== null);
  }, [auth.user.id, consultantsInSite]);

  const loadConsultantsOptions = useDebounceCallback<
    (inputValue: string, callback: (opts: ConsultantOption[]) => void) => void
  >((inputValue, callback) => {
    if (consultantOptions.length > 0) {
      callback(
        consultantOptions.filter(
          (option) =>
            option.label.toLowerCase().includes(inputValue.toLowerCase()) ||
            option.email?.toLowerCase().includes(inputValue.toLowerCase()),
        ),
      );
    } else {
      callback([]);
    }
  }, 500);

  const handleContractTypeChange = useCallback(
    (val: SingleValue<ContractTypesInterface>) => {
      const selectedContractType = val?.value;
      if (selectedContractType == null || typeof selectedContractType !== 'string') {
        return;
      }

      onContractTypeChange(selectedContractType);

      // Default logic for all contract types
      setValue('course_segment', CourseSegment.FULL_COURSE);

      // Overwrite defaults for specific contract types
      if (selectedContractType === 'Workforce Australia') {
        if (selectedCourseAttributes.kind === CourseKindSchema.enum.QUALIFICATION) {
          setValue('course_segment', CourseSegment.FIRST_SEMESTER_ONLY);
        }
      } else if (selectedContractType === 'IEA') {
        // IEA requires full-time enrolment pace
        setValue('work_schedule', WorkSchedule.FULL_TIME);
      }

      const newCategoryOptions = getCategoryOptions(selectedContractType);

      const firstOption = newCategoryOptions[0];
      // Always reset contract_category based on available options
      if (newCategoryOptions.length === 1 && firstOption != null && firstOption?.value !== null) {
        setValue('contract_category', firstOption.value);
      } else if (newCategoryOptions.length === 0) {
        setValue('contract_category', 'n/a');
      } else {
        setValue('contract_category', '');
      }
    },
    [onContractTypeChange, setValue, selectedCourseAttributes.kind],
  );

  const isFullTime = work_schedule === WorkSchedule.FULL_TIME;

  const courseStartAndEndDates = useMemo(() => {
    if (!course?.schedule || isLoading) {
      return null;
    }

    try {
      return getCourseDatesFromAdminCourseSchedule({
        adminCourseScheduleItems: course.schedule,
        courseSegment: course_segment ?? CourseSegment.FULL_COURSE,
        workSchedule: work_schedule ?? WorkSchedule.FULL_TIME,
        startAt: toUtcStartOfDay(start_at_date, 'YYYY-MM-DD'),
      });
    } catch {
      // it's possible for the User to enter invalid data into the input date
      // field and when it happens it causes the course and semester date
      // calculations to throw. When this happens we set the calculated value
      // to null which just removes the expected course completion date
      // from the UI.
      return null;
    }
  }, [course, course_segment, isLoading, start_at_date, work_schedule]);

  const expectedSemesterOneCompletionDate =
    courseStartAndEndDates?.semesters.find((semester) => semester.semester === 1)?.semesterEndDate
      .long ?? null;

  const onSubmit = useCallback(
    (values: EnrolmentSettingsDataTypes) => {
      if (courseReferralModalSnap.mode === CourseReferralModalMode.ADD_SECOND_SEMESTER) {
        const selectedSite = sitesOptions?.find(
          (option) => option.value.site_id === selected_site?.site_id,
        );

        const selectedConsultant = consultantOptions?.find(
          (option) => option.value === selected_consultant?.consultant_id,
        );

        values.selected_site = selectedSite?.value;
        if (
          typeof selectedConsultant?.value === 'string' &&
          typeof selectedConsultant?.label === 'string'
        ) {
          values.selected_consultant = {
            consultant_id: selectedConsultant.value,
            consultant_name: selectedConsultant.label,
          };
        }
      }

      courseReferralModalState.setEnrolmentSettingsData(values);
      goToNextStep();
    },
    [
      courseReferralModalSnap,
      selected_consultant,
      selected_site,
      sitesOptions,
      goToNextStep,
      consultantOptions,
    ],
  );

  const hasSemesterTwo = useMemo(() => {
    return courseHasSemesterTwo(course?.schedule ?? []);
  }, [course]);

  return (
    <Box
      as="form"
      onSubmit={handleSubmit(onSubmit)}
      style={{ width: '100%', height: '100%' }}
      noValidate
    >
      <FormMainContainer>
        <FormContentContainer>
          <FormControl
            isInvalid={!!errors.selected_site}
            isRequired
            hidden={courseReferralModalSnap.mode === CourseReferralModalMode.ADD_SECOND_SEMESTER}
          >
            <FormLabel htmlFor="selected_site">
              <Trans>Site</Trans>
            </FormLabel>
            {isSitesLoading ? (
              <Skeleton height="32px" />
            ) : (
              <Controller
                key={courseReferralModalSnap?.enrolmentSettingsData?.selected_site?.site_id}
                name="selected_site"
                control={control}
                rules={{
                  required: t`Please select a site.`,
                  validate: (value) =>
                    value != null && value.site_id ? true : t`Please select a site.`,
                }}
                render={({ field }) => (
                  <AsyncSelect
                    id="selected_site"
                    name="selected_site"
                    placeholder={t`Select site`}
                    defaultOptions
                    loadOptions={loadSitesOptions}
                    selectedOptionColorScheme={'brand'}
                    value={sitesOptions?.find(
                      (option) => option.value.site_id === field?.value?.site_id,
                    )}
                    onChange={(
                      selectedOption: MultiValue<SiteOption> | SingleValue<SiteOption>,
                    ) => {
                      if (
                        selectedOption != null &&
                        'value' in selectedOption &&
                        selectedOption?.label != null &&
                        selectedOption?.value != null
                      ) {
                        field.onChange(selectedOption?.value);

                        if (
                          sitesOptions.length > 1 &&
                          tenantCtx?.permissions.includes('canCreateReferralsForTeamMembers') ===
                            true
                        ) {
                          setValue('selected_consultant', {
                            consultant_id: '',
                            consultant_name: '',
                          });

                          courseReferralModalState.setConsultant({
                            consultant_id: '',
                            consultant_name: '',
                          });
                        }
                      }
                    }}
                  />
                )}
              />
            )}
            <FormErrorMessage>{errors.selected_site?.message}</FormErrorMessage>
          </FormControl>

          {tenantCtx?.permissions.includes('canCreateReferralsForTeamMembers') === true &&
          selected_site?.site_id ? (
            <FormControl
              isInvalid={!!errors.selected_consultant}
              isRequired
              hidden={courseReferralModalSnap.mode === CourseReferralModalMode.ADD_SECOND_SEMESTER}
            >
              <FormLabel htmlFor="selected_consultant">
                <Trans>Consultant</Trans>
              </FormLabel>
              {isConsultantsLoading ? (
                <Skeleton height="32px" />
              ) : (
                <Controller
                  key={getValues('selected_site')?.site_id}
                  name="selected_consultant"
                  control={control}
                  rules={{
                    required: t`Please select a consultant.`,
                    validate: (value) =>
                      value && value.consultant_id ? true : t`Please select a consultant.`,
                  }}
                  render={({ field }) => (
                    <AsyncSelect
                      id="selected_consultant"
                      name="selected_consultant"
                      placeholder={t`Select consultant`}
                      defaultOptions
                      loadOptions={loadConsultantsOptions}
                      selectedOptionColorScheme={'brand'}
                      components={{ Option }}
                      value={
                        consultantOptions?.find(
                          (option) => option.value === field.value?.consultant_id,
                        ) ?? null
                      }
                      onChange={(
                        selectedOption:
                          | MultiValue<ConsultantOption>
                          | SingleValue<ConsultantOption>,
                      ) => {
                        if (
                          selectedOption != null &&
                          'value' in selectedOption &&
                          selectedOption?.label != null &&
                          selectedOption?.value != null
                        ) {
                          field.onChange({
                            consultant_id: selectedOption?.value,
                            consultant_name: selectedOption?.label,
                          });
                        }
                      }}
                      defaultValue={consultantOptions.find(
                        (option) => option.value === auth.user.id,
                      )}
                    />
                  )}
                />
              )}
              <FormErrorMessage>{errors.selected_consultant?.message}</FormErrorMessage>
            </FormControl>
          ) : null}

          <HStack w="full" justify="space-between" align="start" gap="10px">
            {courseReferralModalSnap.mode === CourseReferralModalMode.ADD_SECOND_SEMESTER ? (
              <FormControl isInvalid={!!errors.semester_2_start_at_date} isRequired>
                <FormLabel htmlFor="semester_2_start_at_date">
                  <Trans>Semester 2 start date</Trans>
                </FormLabel>
                <Input
                  id="semester_2_start_at_date"
                  min={minStartDate.format('YYYY-MM-DD')}
                  {...register('semester_2_start_at_date', {
                    required: t`Start date is required.`,
                    min: {
                      value: minStartDate.format('YYYY-MM-DD'),
                      message: t`Please enter a valid start date. The date cannot be before the end of the first semester and cannot be in the past.`,
                    },
                    validate: (value) => djs(value).isValid() || t`Please enter a valid date.`,
                  })}
                  type="date"
                />
                <FormHelperText fontSize="xs">
                  <Trans>Semester 1 end date is</Trans>{' '}
                  <strong>{expectedSemesterOneCompletionDate}</strong>
                </FormHelperText>
                <FormErrorMessage>{errors.semester_2_start_at_date?.message}</FormErrorMessage>
              </FormControl>
            ) : (
              <FormControl isInvalid={!!errors.start_at_date} isRequired>
                <FormLabel htmlFor="start_at_date">
                  <Trans>Start date</Trans>
                </FormLabel>
                <Input
                  id="start_at_date"
                  min={djs(today).format('YYYY-MM-DD')}
                  {...register('start_at_date', {
                    required: t`Start date is required.`,
                    min: {
                      value: djs(today).format('YYYY-MM-DD'),
                      message: t`Please enter a valid start date. The date should not be in the past.`,
                    },
                    validate: (value) => djs(value).isValid() || t`Please enter a valid date.`,
                  })}
                  type="date"
                  defaultValue={djs(
                    courseReferralModalSnap.enrolmentSettingsData.start_at_date,
                  ).format('YYYY-MM-DD')}
                />
                <FormErrorMessage>{errors.start_at_date?.message}</FormErrorMessage>
              </FormControl>
            )}

            <FormControl
              isInvalid={!!errors.external_id}
              isRequired={tenantCtx?.settings?.is_external_id_field_mandatory}
            >
              <FormLabel htmlFor="external_id">
                {courseReferralModalSnap.mode === CourseReferralModalMode.ADD_SECOND_SEMESTER ? (
                  <Trans>Purchase order for Semester 2</Trans>
                ) : (
                  <Trans>Purchase order (or unique identifier)</Trans>
                )}
              </FormLabel>
              <Input
                id="external_id"
                {...register(
                  courseReferralModalSnap.mode === CourseReferralModalMode.ADD_SECOND_SEMESTER
                    ? 'semesterTwoExternalId'
                    : 'external_id',
                  {
                    required:
                      tenantCtx?.settings?.is_external_id_field_mandatory == true &&
                      t`Purchase order (or unique identifier) is required.`,
                    setValueAs: (value: string) => {
                      if (typeof value === 'string') {
                        const trimmed = value.trim();
                        return trimmed === '' ? undefined : trimmed;
                      }
                      return value;
                    },
                  },
                )}
                type="text"
                spellCheck="false"
                autoComplete="off"
                onChange={(e) => {
                  setValue('external_id', e.target.value);
                }}
              />
              <FormErrorMessage>
                {courseReferralModalSnap.mode === CourseReferralModalMode.ADD_SECOND_SEMESTER
                  ? errors.semesterTwoExternalId?.message
                  : errors.external_id?.message}
              </FormErrorMessage>
            </FormControl>
          </HStack>

          <HStack
            w="full"
            justify="space-between"
            align="start"
            gap="10px"
            hidden={courseReferralModalSnap.mode === CourseReferralModalMode.ADD_SECOND_SEMESTER}
          >
            <FormControl isInvalid={!!errors.enrolment_contract_type} isRequired>
              <FormLabel htmlFor="enrolment_contract_type">
                <Trans>Contract type</Trans>
              </FormLabel>
              <Controller
                name="enrolment_contract_type"
                control={control}
                rules={{ required: t`Contract type is required.` }}
                render={({ field }) => (
                  <Select
                    id="enrolment_contract_type"
                    placeholder={t`Select option`}
                    options={ContractTypes}
                    value={ContractTypes.find((option) => option.value === field.value)}
                    onChange={handleContractTypeChange}
                  />
                )}
              />
              <FormErrorMessage>{errors.enrolment_contract_type?.message}</FormErrorMessage>
            </FormControl>

            {enrolment_contract_type && categoryOptions.length > 0 ? (
              <FormControl
                isInvalid={!!errors.contract_category}
                isRequired
                isDisabled={categoryOptions.length < 1}
              >
                <FormLabel htmlFor="contract_category">
                  <Trans>Contract category</Trans>
                </FormLabel>
                <Controller
                  name="contract_category"
                  control={control}
                  rules={{ required: t`Contract category is required.` }}
                  render={({ field }) => (
                    <Select
                      key={getValues('contract_category')}
                      id="contract_category"
                      placeholder={t`Select option`}
                      options={categoryOptions}
                      value={categoryOptions.find((option) => option.value === field.value)}
                      onChange={(val: SingleValue<CategoryOptionsInterface>) => {
                        field.onChange(val?.value);
                      }}
                      isReadOnly={categoryOptions.length === 1}
                    />
                  )}
                />
                <FormErrorMessage>{errors.contract_category?.message}</FormErrorMessage>
              </FormControl>
            ) : (
              <FormControl />
            )}
          </HStack>

          <HStack w="full" justify="space-between" align="start" gap="10px">
            {hasSemesterTwo ? (
              <FormControl isInvalid={!!errors.course_segment} isRequired>
                <FormLabel htmlFor="course_segment">
                  <Trans>Course segment</Trans>
                </FormLabel>
                {courseReferralModalSnap.mode === CourseReferralModalMode.CREATE_NEW ||
                courseReferralModalSnap.mode ===
                  CourseReferralModalMode.CREATE_NEW_FOR_SELECTED_COURSE ? (
                  <Controller
                    name="course_segment"
                    control={control}
                    rules={{ required: t`Course segment is required.` }}
                    render={({ field }) => (
                      <Select
                        id="course_segment"
                        placeholder={t`Select option`}
                        options={CourseSegmentOptions}
                        value={CourseSegmentOptions.find((option) => option.value === field.value)}
                        isDisabled={enrolment_contract_type === 'IEA'}
                        onChange={(val: CourseSegmentOptionsInterface) =>
                          field.onChange(val?.value)
                        }
                      />
                    )}
                  />
                ) : (
                  <Controller
                    name="course_segment"
                    control={control}
                    rules={{ required: t`Course segment is required.` }}
                    render={({ field }) => {
                      if (field.value !== CourseSegment.FULL_COURSE) {
                        field.onChange(CourseSegment.FULL_COURSE);
                      }

                      return (
                        <Select
                          id="course_segment"
                          placeholder={t`Select option`}
                          value={{
                            label: '2nd Semester',
                            value: CourseSegment.FULL_COURSE,
                          }}
                          isDisabled
                        />
                      );
                    }}
                  />
                )}
                <FormErrorMessage>{errors.course_segment?.message}</FormErrorMessage>
              </FormControl>
            ) : null}

            <FormControl
              isRequired
              hidden={courseReferralModalSnap.mode === CourseReferralModalMode.ADD_SECOND_SEMESTER}
            >
              <FormLabel>
                <Trans>Enrolment pace</Trans>
              </FormLabel>
              <HStack align="center">
                <ButtonGroup spacing={0} isAttached>
                  <Button
                    colorScheme={isFullTime ? 'brand' : 'gray'}
                    onClick={() => setValue('work_schedule', WorkSchedule.FULL_TIME)}
                    variant={isFullTime ? 'solid' : 'ghost'}
                    isDisabled={enrolment_contract_type === 'IEA' || !enrolment_contract_type}
                  >
                    <Trans>Full-time</Trans>
                  </Button>
                  <Button
                    colorScheme={!isFullTime ? 'brand' : 'gray'}
                    onClick={() => setValue('work_schedule', WorkSchedule.PART_TIME)}
                    variant={!isFullTime ? 'solid' : 'ghost'}
                    isDisabled={enrolment_contract_type === 'IEA' || !enrolment_contract_type}
                  >
                    <Trans>Part-time</Trans>
                  </Button>
                </ButtonGroup>
                {!isFullTime && (
                  <Tooltip
                    label={
                      <Box
                        bg="bg.surface"
                        borderRadius="8px"
                        color="fg.default"
                        borderWidth="1px"
                        borderColor="border.default"
                        p="8px"
                        fontSize="sm"
                      >
                        <Trans>
                          Please note, by selecting part-time no outcome can be claimed.
                        </Trans>
                      </Box>
                    }
                    fontSize="md"
                    colorScheme="warning"
                    placement="bottom"
                    gutter={16}
                    bg="transparent"
                    shadow="none"
                  >
                    <Icon as={AlertCircleIcon} color="warning.emphasis" boxSize="20px" />
                  </Tooltip>
                )}
              </HStack>
            </FormControl>
          </HStack>
          {courseStartAndEndDates?.courseEndDate ? (
            <Text color="fg.emphasis" textStyle="sm" textAlign="left" fontWeight="normal" w="full">
              <Trans>Course is expected to end by</Trans>{' '}
              {courseStartAndEndDates?.courseEndDate.long}
            </Text>
          ) : null}
        </FormContentContainer>
        <FormFooterContainer>
          <Button
            rightIcon={<ChevronRightIcon />}
            isLoading={isSubmitting || isConsultantsLoading || isSitesLoading}
            type="submit"
          >
            <Trans>Next</Trans>
          </Button>
          <Button onClick={goToPrevStep} variant="tertiary" leftIcon={<ChevronLeftIcon />}>
            <Trans>Back</Trans>
          </Button>
        </FormFooterContainer>
      </FormMainContainer>
    </Box>
  );
}
